import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, apiUpload, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Shield, Check, X, Trash2, Users, ArrowRightLeft, ClipboardList, Pencil, Plus,
  Radio, BadgeCheck, UserCog, LayoutGrid, BookOpen, ExternalLink, RefreshCw, Handshake,
  MessageSquare, Send, Trophy, Calendar, Swords, Lock, Unlock, Timer, Filter,
  Download, Upload, ChevronDown, ChevronRight, Hash, Loader2,
} from "lucide-react";
import VerifyTicker from "@/components/VerifyTicker";
import AdminDiscordHealth from "./admin/AdminDiscordHealth";
import AdminSponsors from "./admin/AdminSponsors";
import AdminTeamsTable from "./admin/AdminTeamsTable";

const TABS = [
  { key: "apps",     label: "Team Applications", icon: ClipboardList, officialAllowed: false },
  { key: "teams",    label: "Teams",             icon: Users,         officialAllowed: true  },
  { key: "rosters",  label: "Rosters",           icon: Pencil,        officialAllowed: true  },
  { key: "divisions",label: "Divisions",         icon: LayoutGrid,    officialAllowed: false },
  { key: "rounds",   label: "Rounds",            icon: Swords,        officialAllowed: false },
  { key: "transfers",label: "Transfers",         icon: ArrowRightLeft,officialAllowed: false },
  { key: "streamers",label: "Streamers",         icon: Radio,         officialAllowed: false },
  { key: "sponsors", label: "Sponsors",          icon: Handshake,     officialAllowed: false },
  { key: "discord",  label: "Discord Health",    icon: MessageSquare, officialAllowed: false },
  { key: "users",    label: "Users",             icon: UserCog,       officialAllowed: false },
];

const ROLE_COLORS = {
  admin:    "text-[#FF007F] border-[#FF007F]",
  rep:      "text-[#F4D03F] border-[#F4D03F]",
  streamer: "text-[#B084FF] border-[#B084FF]",
  member:   "text-gray-400 border-[#232330]",
};

export default function Admin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Iter99 · League Officials can access a RESTRICTED view of this page —
  // Teams + Rosters tabs only. Anything destructive still writes to the
  // moderation_log on the backend regardless of role. Backend role string
  // is "official" (see core.py require_admin_or_official).
  const isOfficial = user?.role === "official";
  const canAdmin = isAdmin || isOfficial;
  // Officials start on `teams` since `apps` (approvals) is admin-only.
  const [tab, setTab] = useState(isAdmin ? "apps" : "teams");
  const [pending, setPending] = useState([]);
  const [teams, setTeams] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [players, setPlayers] = useState([]);
  const [streamers, setStreamers] = useState([]);
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [divForm, setDivForm] = useState({ name: "", tier: "Mains", color: "#00F0FF", sort_order: 0, description: "" });
  const [assignTargetDiv, setAssignTargetDiv] = useState(null);
  const [assignTeamId, setAssignTeamId] = useState("");
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDestDivId, setMoveDestDivId] = useState("");
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [rosterFor, setRosterFor] = useState(null);
  const [rosterEdit, setRosterEdit] = useState([]);
  const [verifyFor, setVerifyFor] = useState(null);
  const [verifyRows, setVerifyRows] = useState([]);
  const [urlDraft, setUrlDraft] = useState({});
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [discord, setDiscord] = useState({ configured: false, url_preview: "", channels: {} });
  const [discordHealth, setDiscordHealth] = useState(null);
  const [season, setSeason] = useState({ target_at: "", label: "", message: "" });
  const [seasonDraft, setSeasonDraft] = useState({ target_at: "", label: "", message: "" });
  const [discordDraft, setDiscordDraft] = useState("");
  const [discordChannels, setDiscordChannels] = useState({ live: "", results: "", standings: "", predictions: "", schedule: "" });
  const [showDiscordChannels, setShowDiscordChannels] = useState(false);
  const [rounds, setRounds] = useState([]);
  const [roundWeekend, setRoundWeekend] = useState("");
  const [roundName, setRoundName] = useState("");
  const [roundMatchups, setRoundMatchups] = useState([{ home_team_id: "", away_team_id: "" }]);
  // Iter94 · Expandable rounds · per-round fixtures + bulk thread open.
  const [roundExpand, setRoundExpand] = useState({}); // {roundNumber: boolean}
  const [roundFixtures, setRoundFixtures] = useState({}); // {roundNumber: fixtures[]}
  const [roundFixturesBusy, setRoundFixturesBusy] = useState({});
  const [bulkThreadBusy, setBulkThreadBusy] = useState({});
  const [threadBusyPerFx, setThreadBusyPerFx] = useState({});
  // Iter60 · Bulk round reschedule
  const [reschedRound, setReschedRound] = useState("");
  const [reschedTier, setReschedTier] = useState("");
  const [reschedStart, setReschedStart] = useState("");
  const [reschedEnd, setReschedEnd] = useState("");
  // Iter64 · Dedupe R2-R11 duplicate fixtures
  const [dedupeFrom, setDedupeFrom] = useState("2");
  const [dedupeTo, setDedupeTo] = useState("11");
  const [dedupeResult, setDedupeResult] = useState(null);
  // Iter71 · Roster duplicates scanner
  const [rosterDupes, setRosterDupes] = useState(null);
  const [rosterDupesBusy, setRosterDupesBusy] = useState(false);
  // Iter64 · Repost season slates to team chats
  const [repostTier, setRepostTier] = useState("");
  const [reposting, setReposting] = useState(false);
  const [repostResult, setRepostResult] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorForm, setSponsorForm] = useState({ title: "", url: "", sort_order: 0, file: null });
  // Iter75 · must live above the early returns so react-hooks/rules-of-hooks holds
  const [bulkExportBusy, setBulkExportBusy] = useState(false);
  // Iter80 · Pending-approval result queue (admins / officials finalize these)
  const [pendingApproval, setPendingApproval] = useState([]);
  const sponsorFileRef = React.useRef(null);

  const load = async () => {
    // Iter99 · Officials only have access to a few of these admin endpoints,
    // so swallow 403s silently per-call instead of letting Promise.all reject
    // and surface uncaught runtime errors in the UI.
    const safe = (p) => p.catch(() => ({ data: [] }));
    const [ap, all, tr, pl, st, us, dv, sp, rd] = await Promise.all([
      safe(api.get("/admin/teams?status=pending")),
      safe(api.get("/admin/teams")),
      safe(api.get("/transfers")),
      safe(api.get("/players?limit=10000")),
      safe(api.get("/streamers")),
      safe(api.get("/admin/users")),
      safe(api.get("/divisions")),
      safe(api.get("/sponsors")),
      safe(api.get("/rounds")),
    ]);
    setPending(ap.data);
    setTeams(all.data);
    setTransfers(tr.data);
    setPlayers(pl.data);
    setStreamers(st.data);
    setUsers(us.data);
    setDivisions(dv.data);
    setSponsors(sp.data || []);
    setRounds(rd.data || []);
    // Iter80 · Pending approval queue (silent failure for non-admins)
    try {
      const { data: pa } = await api.get("/admin/fixtures/pending-approval");
      setPendingApproval(pa || []);
    } catch { /* no-op */ }
    // Iter44 · Season countdown (public endpoint, safe to fail silently)
    try {
      const { data: sc } = await api.get("/season/countdown");
      setSeason(sc);
      setSeasonDraft({ target_at: sc.target_at || "", label: sc.label || "", message: sc.message || "" });
    } catch { /* no-op */ }
  };

  // Iter44 · Save the season countdown target (admin/official)
  const saveSeasonCountdown = async () => {
    try {
      // Convert local datetime-input value (no TZ) to ISO Z if present
      let target = (seasonDraft.target_at || "").trim();
      if (target && !target.endsWith("Z") && !target.includes("+")) target = new Date(target).toISOString();
      const { data } = await api.put("/admin/season/countdown", {
        target_at: target || null,
        label: seasonDraft.label,
        message: seasonDraft.message,
      });
      setSeason(data);
      toast.success(target ? "Season countdown scheduled." : "Season countdown cleared.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Iter80 · Pending approval actions — admin/official only
  const approveResult = async (f) => {
    try {
      await api.post(`/fixtures/${f.id}/approve-result`);
      toast.success(`Approved · ${f.proposed_result?.home_score}-${f.proposed_result?.away_score}.`);
      load().catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approve failed"); }
  };
  const rejectResult = async (f) => {
    if (!window.confirm("Reject this result and send it back to the reps?")) return;
    try {
      await api.post(`/fixtures/${f.id}/reject-result`);
      toast.success("Result rejected — reps can re-submit.");
      load().catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reject failed"); }
  };

  const createRound = async () => {
    if (!roundWeekend) { toast.error("Pick a weekend date."); return; }
    const clean = roundMatchups.filter((m) => m.home_team_id && m.away_team_id && m.home_team_id !== m.away_team_id);
    if (clean.length === 0) { toast.error("Add at least one valid matchup."); return; }
    try {
      const { data } = await api.post("/admin/rounds", {
        name: roundName || undefined,
        weekend_start: new Date(roundWeekend).toISOString(),
        matchups: clean,
      });
      toast.success(`Round ${data.round.number} created — ${data.fixtures} matchups`);
      setRoundName("");
      setRoundMatchups([{ home_team_id: "", away_team_id: "" }]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteRound = async (r) => {
    if (!window.confirm(`Delete ${r.name}? All fixtures inside it will also be wiped.`)) return;
    try {
      await Promise.all((r.fixture_ids || []).map((fid) => api.delete(`/fixtures/${fid}`).catch(() => null)));
      // Rounds are a lightweight index; wipe the doc too.
      await api.delete(`/admin/rounds/${r.id}`).catch(() => null);
      toast.success(`Round ${r.number} deleted.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Iter94 · Admin › Rounds helpers · expand to show fixtures + open threads
  const toggleRoundExpand = async (rn) => {
    const next = !roundExpand[rn];
    setRoundExpand((s) => ({ ...s, [rn]: next }));
    if (next && !roundFixtures[rn]) {
      setRoundFixturesBusy((s) => ({ ...s, [rn]: true }));
      try {
        const { data } = await api.get(`/fixtures`, { params: { round_number: rn } });
        setRoundFixtures((s) => ({ ...s, [rn]: data || [] }));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load fixtures");
      } finally {
        setRoundFixturesBusy((s) => ({ ...s, [rn]: false }));
      }
    }
  };

  const reloadRoundFixtures = async (rn) => {
    try {
      const { data } = await api.get(`/fixtures`, { params: { round_number: rn } });
      setRoundFixtures((s) => ({ ...s, [rn]: data || [] }));
    } catch (e) { /* ignore */ }
  };

  const openSingleThread = async (fx) => {
    setThreadBusyPerFx((s) => ({ ...s, [fx.id]: true }));
    try {
      const { data } = await api.post(`/fixtures/${fx.id}/discord-thread`);
      if (data.created) {
        toast.success(`Thread opened for ${fx.id.slice(0, 6)}`);
      } else {
        toast.info("Thread was already open");
      }
      await reloadRoundFixtures(fx.round_number);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Thread create failed");
    } finally {
      setThreadBusyPerFx((s) => ({ ...s, [fx.id]: false }));
    }
  };

  // Iter98 · Discord threads can be deleted manually on Discord but we still
  // store the stale thread_id. "Recreate" clears the stored id and spawns a
  // fresh thread via the admin-only reopen endpoint.
  const recreateThread = async (fx) => {
    const label = `${fx.home_team_id?.slice(0, 4) || "??"} vs ${fx.away_team_id?.slice(0, 4) || "??"}`;
    if (!window.confirm(`Recreate the Discord thread for ${label}? The old thread link will be lost — only do this if the thread was deleted on Discord.`)) {
      return;
    }
    setThreadBusyPerFx((s) => ({ ...s, [fx.id]: true }));
    try {
      await api.post(`/fixtures/${fx.id}/discord-thread/reopen`);
      toast.success("Fresh thread spawned");
      await reloadRoundFixtures(fx.round_number);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recreate failed");
    } finally {
      setThreadBusyPerFx((s) => ({ ...s, [fx.id]: false }));
    }
  };

  const openAllThreadsForRound = async (rn) => {
    setBulkThreadBusy((s) => ({ ...s, [rn]: true }));
    try {
      const { data } = await api.post(`/admin/rounds/${rn}/open-threads`);
      const createdN = data.created?.length || 0;
      const existingN = data.existing?.length || 0;
      const errorsN = data.errors?.length || 0;
      let msg = `R${rn} · ${createdN} new thread${createdN === 1 ? "" : "s"} opened`;
      if (existingN) msg += ` · ${existingN} already open`;
      if (errorsN) msg += ` · ${errorsN} failed`;
      if (errorsN) toast.error(msg);
      else toast.success(msg);
      await reloadRoundFixtures(rn);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk thread open failed");
    } finally {
      setBulkThreadBusy((s) => ({ ...s, [rn]: false }));
    }
  };

  // Iter97 · CSV export for referee hand-offs. Uses an authenticated axios
  // call → blob → object-URL download so the admin's auth cookie flows through.
  const downloadRoundsCsv = async (rn /* null = all rounds */) => {
    try {
      const url = rn != null ? `/admin/rounds/${rn}/export.csv` : `/admin/rounds/export.csv`;
      const res = await api.get(url, { responseType: "blob" });
      const match = /filename="([^"]+)"/.exec(res.headers?.["content-disposition"] || "");
      const filename = match ? match[1] : (rn != null ? `legacy-round-${rn}.csv` : `legacy-rounds-all.csv`);
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(rn != null ? `R${rn} CSV downloaded` : "All rounds CSV downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "CSV export failed");
    }
  };

  // Iter64 · Scan/apply dedupe of duplicate fixtures
  const runDedupe = async (apply) => {
    const from = parseInt(dedupeFrom, 10) || 2;
    const to = parseInt(dedupeTo, 10) || 11;
    if (to < from) { toast.error("Invalid range"); return; }
    if (apply && !window.confirm(`Delete every duplicate fixture in R${from}-R${to}? Keeps one per team pair per round. R1 is untouched.`)) return;
    try {
      const { data } = await api.post(`/admin/fixtures/dedupe?from_round=${from}&to_round=${to}&apply=${apply}`);
      setDedupeResult(data);
      if (apply) {
        toast.success(`Deleted ${data.deleted} duplicate${data.deleted === 1 ? "" : "s"} across ${data.duplicate_groups} group${data.duplicate_groups === 1 ? "" : "s"}.`);
        load();
      } else if (data.duplicate_groups === 0) {
        toast.success("No duplicates found — schedule is clean.");
      } else {
        toast.warning(`Found ${data.duplicate_groups} duplicate group${data.duplicate_groups === 1 ? "" : "s"} · ${data.would_delete} extra${data.would_delete === 1 ? "" : "s"} — click Apply to remove.`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Dedupe failed"); }
  };

  // Iter71 · Roster duplicates scan
  const runRosterDupes = async () => {
    setRosterDupesBusy(true);
    try {
      const { data } = await api.get("/admin/rosters/duplicates");
      setRosterDupes(data);
      const totals = data.totals || {};
      const total = (totals.cross_team_handle || 0) + (totals.cross_team_hll || 0) + (totals.intra_team_handle || 0);
      if (total === 0) toast.success(`Rosters clean — scanned ${data.scanned_players} players across ${data.scanned_teams} teams.`);
      else toast.warning(`${total} duplicate group${total === 1 ? "" : "s"} found across ${data.scanned_teams} teams — review below.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Roster scan failed");
    } finally {
      setRosterDupesBusy(false);
    }
  };

  // Iter64 · Repost every team's slate to their Discord team channel
  const repostTeamSlates = async () => {
    if (!window.confirm(`Repost every team's season slate to their Discord team channel${repostTier ? ` (${repostTier} only)` : ""}? Teams without a channel are skipped.`)) return;
    setReposting(true);
    try {
      const { data } = await api.post("/admin/season/repost-team-slates", { tier: repostTier || undefined });
      setRepostResult(data);
      if (data.posted > 0) {
        toast.success(`Reposted to ${data.posted} team channel${data.posted === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}.`);
      } else {
        toast.warning(`No slates posted. ${data.skipped} team${data.skipped === 1 ? "" : "s"} skipped. ${data.detail || ""}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Repost failed"); }
    finally { setReposting(false); }
  };


  const rescheduleRound = async () => {
    const rn = parseInt(reschedRound, 10);
    if (!rn || rn < 1) { toast.error("Enter a valid round number."); return; }
    if (!reschedStart || !reschedEnd) { toast.error("Pick both start and end dates."); return; }
    if (reschedEnd < reschedStart) { toast.error("End date must be on or after start date."); return; }
    const scope = reschedTier ? `${reschedTier} R${rn}` : `all R${rn} fixtures (both tiers)`;
    if (!window.confirm(`Push ${scope} into ${reschedStart} → ${reschedEnd}? A notice will be posted in every affected match thread.`)) return;
    try {
      const { data } = await api.post("/admin/rounds/reschedule", {
        round_number: rn,
        tier: reschedTier || undefined,
        start_date: reschedStart,
        end_date: reschedEnd,
      });
      if (data.updated === 0) {
        toast.warning(data.detail || "No fixtures matched.");
      } else {
        toast.success(`R${rn} · ${data.updated} fixture${data.updated === 1 ? "" : "s"} pushed into ${reschedStart} → ${reschedEnd}.`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { if (canAdmin) load(); }, [canAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/sync/status").then((r) => setSyncStatus(r.data)).catch(() => setSyncStatus(null));
    api.get("/admin/discord/webhook").then((r) => setDiscord(r.data)).catch(() => {});
  }, [isAdmin]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/streamers/sync");
      const total = (data.twitch_live || 0) + (data.youtube_live || 0);
      toast.success(`Live sync complete — ${total} streamer${total === 1 ? "" : "s"} live`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const saveDiscordWebhook = async () => {
    try {
      const payload = { channels: discordChannels };
      if (discordDraft !== "") payload.url = discordDraft;
      const { data } = await api.put("/admin/discord/webhook", payload);
      toast.success(data.configured ? "Discord webhook saved." : "Discord webhook cleared.");
      setDiscord(data);
      setDiscordDraft("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const postDiscord = async (kind) => {
    try {
      await api.post(`/admin/discord/post-${kind}`);
      const label = ({
        leaderboard: "Predictions leaderboard",
        "weekly-top-predictors": "Weekly top predictors",
        schedule: "Schedule",
        standings: "Standings",
        results: "Results",
        "live-streams": "Live streams",
      })[kind] || kind;
      toast.success(`${label} posted to Discord`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!user) return <div className="text-gray-500">Loading…</div>;
  if (!canAdmin) return <Navigate to="/dashboard" replace />;

  const teamById = (id) => teams.find((t) => t.id === id);
  const playerById = (id) => players.find((p) => p.id === id);
  const approvedTeams = teams.filter((t) => (t.status || "approved") === "approved");
  const declinedTeams = teams.filter((t) => t.status === "declined");

  const approve = async (id) => {
    try { await api.post(`/admin/teams/${id}/approve`); toast.success("Team approved."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitDecline = async () => {
    if (!declineFor) return;
    try {
      await api.post(`/admin/teams/${declineFor.id}/decline`, { reason: declineReason });
      toast.success("Team declined.");
      setDeclineFor(null); setDeclineReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteTeam = async (t) => {
    if (!window.confirm(`Delete ${t.name}? This wipes their fixtures, transfers, rosters and applications.`)) return;
    try { await api.delete(`/admin/teams/${t.id}`); toast.success(`${t.name} removed.`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteTransfer = async (tr) => {
    if (!window.confirm(`Delete this transfer entry?`)) return;
    try { await api.delete(`/admin/transfers/${tr.id}`); toast.success("Transfer removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openRosterEditor = (t) => {
    const roster = players.filter((p) => p.team_id === t.id);
    setRosterFor(t);
    setRosterEdit(roster.length
      ? roster.map((p) => ({
          handle: p.handle || "",
          player_id: p.player_id || "",
          real_name: p.real_name || "",
          role: p.role || "",
          country: p.country || "",
          hll_profile_url: p.hll_profile_url || "",
          email: p.email || "",
          is_rep: !!p.is_rep,
        }))
      : [{ handle: "", player_id: "", email: "", is_rep: false }]);
  };

  // Iter74 · Admin export/replace helpers (single team)
  const exportTeamRoster = async (t) => {
    try {
      const r = await api.get(`/teams/${t.id}/roster.cvc`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(t.tag || "roster").toUpperCase().replace(/\s+/g, "_")}_roster.cvc`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${t.tag} roster.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed");
    }
  };
  const replaceTeamRoster = async (t, file) => {
    if (!file) return;
    if (!window.confirm(`Replace entire ${t.tag} roster with ${file.name}? Current players will be deleted.`)) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiUpload(`/teams/${t.id}/roster-csv`, fd);
      toast.success(`${t.tag} roster replaced · ${(data.imported || []).length || data.roster_size || 0} players imported.`);
      load().catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Replace failed");
    }
  };

  // Iter75 · Zip every approved team's CVC into one download for season archival.
  const exportAllRosters = async () => {
    setBulkExportBusy(true);
    try {
      const r = await api.get(`/admin/rosters/export-all.zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().replace(/[-:]/g, "").slice(0, 13);
      link.download = `legacyhll_rosters_${stamp}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("All rosters archived.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk export failed");
    } finally {
      setBulkExportBusy(false);
    }
  };
  const submitAdminRoster = async () => {
    const valid = rosterEdit.filter((r) => (r.handle || "").trim());
    if (valid.length === 0) { toast.error("Add at least one player."); return; }
    // Iter79 · Rep access via tick-box only — no email required. Backend auto-links
    // the rep to the matching registered user on their next login.
    const repCount = valid.filter((r) => r.is_rep).length;
    if (repCount > 4) { toast.error(`Max 4 reps per team. You've marked ${repCount}.`); return; }
    // Strip empty-string emails to null so Pydantic's EmailStr validator doesn't
    // reject them on non-rep rows (empty string is NOT a valid EmailStr).
    const payload = valid.map((r) => ({
      ...r,
      email: (r.email || "").trim() || null,
      player_id: (r.player_id || "").trim() || null,
      real_name: (r.real_name || "").trim() || null,
      role: (r.role || "").trim() || null,
      country: (r.country || "").trim() || null,
      hll_profile_url: (r.hll_profile_url || "").trim() || null,
    }));
    try {
      const { data } = await api.put(`/admin/teams/${rosterFor.id}/roster`, { players: payload });
      toast.success(`Roster overwritten · ${data.roster_size} players locked in.`);
      setRosterFor(null);
      // Fire-and-forget — any failure inside load() must NOT leak back and
      // crash the dialog's exit transition (that's the source of the
      // "screen turns black and nothing happens" symptom).
      load().catch(() => { /* already toasted elsewhere */ });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to overwrite roster");
    }
  };
  const openVerification = async (t) => {
    setVerifyFor(t);
    setVerifyRows([]);
    setUrlDraft({});
    try {
      const { data } = await api.get(`/admin/teams/${t.id}/verify`);
      setVerifyRows(data.players || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load verification"); }
  };
  const reloadVerify = async () => {
    if (!verifyFor) return;
    try {
      const { data } = await api.get(`/admin/teams/${verifyFor.id}/verify`);
      setVerifyRows(data.players || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveHllUrl = async (pid) => {
    const url = (urlDraft[pid] || "").trim();
    if (!url) { toast.error("Enter a URL or handle"); return; }
    try {
      await api.post(`/admin/players/${pid}/hll-url`, { hll_profile_url: url });
      toast.success("HLL URL saved");
      setUrlDraft({ ...urlDraft, [pid]: "" });
      reloadVerify();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleVerified = async (pid) => {
    try {
      const { data } = await api.post(`/admin/players/${pid}/hll-verify`);
      toast.success(data.hll_verified ? "Player verified" : "Verification cleared");
      reloadVerify();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const refreshHll = async (pid) => {
    try {
      await api.post(`/admin/players/${pid}/hll-refresh`);
      toast.success("HLL stats refreshed");
      reloadVerify();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const verifyAllTeam = async (t) => {
    try {
      const { data } = await api.post(`/admin/teams/${t.id}/verify-all`);
      if (data.newly_verified === 0) {
        toast.success(`${t.tag} — already fully verified (${data.roster_size} players)`);
      } else {
        toast.success(`${t.tag} — verified ${data.newly_verified} player${data.newly_verified === 1 ? "" : "s"} (${data.roster_size} total)`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Iter39 · Recompute season standings (new scoring rule: pts = your score)
  const recomputeStandings = async () => {
    if (!window.confirm("Recompute all standings from completed fixtures using the current scoring rule (points = your score, valid only for 5-0/4-1/3-2)? This resets W/L/points on every team then replays.")) return;
    try {
      const { data } = await api.post("/admin/standings/recompute");
      toast.success(`Standings recomputed · ${data.applied} fixture${data.applied === 1 ? "" : "s"} applied${data.skipped ? ` · ${data.skipped} skipped (invalid score)` : ""}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Iter39 · Flip a team between Mains ↔ Lite (auto-adjusts roster_cap)
  const switchTier = async (t) => {
    const nextTier = t.tier === "Mains" ? "Lite" : "Mains";
    const nextCap = nextTier === "Mains" ? 150 : 100;
    if (!window.confirm(`Switch ${t.tag} from ${t.tier || "Mains"} → ${nextTier}? Roster cap becomes ${nextCap}.`)) return;
    try {
      const { data } = await api.post(`/admin/teams/${t.id}/switch-tier`);
      if (data.over_cap_by > 0) {
        toast.warning(`${t.tag} → ${data.tier} (cap ${data.roster_cap}). Roster is over cap by ${data.over_cap_by} — trim before matchday.`);
      } else {
        toast.success(`${t.tag} moved to ${data.tier} (cap ${data.roster_cap}).`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Iter41 · Manual Discord sync for a team (idempotent)
  const discordSyncTeam = async (t) => {
    try {
      const { data } = await api.post(`/admin/teams/${t.id}/discord-sync`);
      const bits = [];
      if (data.role_created) bits.push("team role");
      if (data.rep_role_created) bits.push("rep role");
      if (data.channel_created) bits.push("channel");
      const created = bits.length ? `created ${bits.join(" + ")}` : "already synced";
      const assigned = data.assigned ? ` · ${data.assigned} rep${data.assigned === 1 ? "" : "s"} auto-assigned` : "";
      if (data.errors?.length) {
        toast.warning(`${t.tag} · ${created}${assigned} · ${data.errors.join("; ")}`);
      } else {
        toast.success(`${t.tag} · ${created}${assigned}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setDiscordRepRole = async (t) => {
    const current = t.discord_rep_role_id || "";
    const val = window.prompt(
      `Discord Rep Role ID for ${t.tag}\n\n(17-20 digit snowflake — right-click role in Discord → Copy Role ID)\nLeave blank to clear the override and fall back to the auto-provisioned role.`,
      current,
    );
    if (val === null) return;
    try {
      await api.put(`/admin/teams/${t.id}/discord-rep-role`, { discord_rep_role_id: val.trim() });
      toast.success(val.trim() ? `${t.tag} · rep role updated` : `${t.tag} · rep role cleared`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const verifyAllTeams = async () => {
    if (!window.confirm(`Verify every player on all ${approvedTeams.length} approved teams? This can't be undone from here — you'd have to un-verify individually.`)) return;
    let ok = 0, newly = 0;
    for (const t of approvedTeams) {
      try {
        const { data } = await api.post(`/admin/teams/${t.id}/verify-all`);
        ok += 1;
        newly += data.newly_verified || 0;
      } catch { /* keep marching */ }
    }
    toast.success(`Bulk verify complete · ${ok}/${approvedTeams.length} teams · ${newly} players newly verified`);
    load();
  };

  // Iter38 · Roster lock (per-team + bulk)
  const toggleRosterLock = async (t) => {
    const wantLock = !t.roster_locked;
    if (wantLock && !window.confirm(`Lock roster for ${t.tag}? Reps won't be able to add/remove/edit players until it's unlocked.`)) return;
    try {
      const { data } = await api.post(`/admin/teams/${t.id}/${wantLock ? "lock-roster" : "unlock-roster"}`);
      toast.success(data.roster_locked ? `${t.tag} — roster locked` : `${t.tag} — roster unlocked`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const lockAllRosters = async () => {
    if (!window.confirm(`Lock rosters on all ${approvedTeams.length} approved teams? Reps cannot add/remove players until unlocked.`)) return;
    try {
      const { data } = await api.post("/admin/teams/lock-all-rosters");
      toast.success(`Locked ${data.locked} team roster${data.locked === 1 ? "" : "s"}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  // Iter43 · Season lockdown toggle per division
  const toggleSeasonLock = async (d) => {
    const goLock = !d.season_locked;
    const msg = goLock
      ? `Lock the season for '${d.name}'? Every team's roster will be frozen and tier switches will be blocked until you unlock.`
      : `Unlock the season for '${d.name}'? All teams' rosters in this division will be released.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/admin/divisions/${d.id}/${goLock ? "season-lock" : "season-unlock"}`);
      const count = data.teams_locked ?? data.teams_unlocked ?? 0;
      toast.success(`${d.name} · ${goLock ? "season locked" : "season unlocked"} · ${count} team${count === 1 ? "" : "s"} affected`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Iter43 · Discord Health panel loader
  const loadDiscordHealth = async () => {
    try {
      const { data } = await api.get("/admin/discord/health");
      setDiscordHealth(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const unlockAllRosters = async () => {
    try {
      const { data } = await api.post("/admin/teams/unlock-all-rosters");
      toast.success(`Unlocked ${data.unlocked} team roster${data.unlocked === 1 ? "" : "s"}.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Iter42 · Bulk Discord sync — provisions role + rep role + channel for every approved team.
  const discordSyncAll = async () => {
    if (!window.confirm(`Run Discord sync on all ${approvedTeams.length} approved teams? Idempotent — teams already synced won't be duplicated.`)) return;
    try {
      const { data } = await api.post("/admin/teams/discord-sync-all");
      const msg = `Discord sync · ${data.synced}/${data.total} synced${data.failed ? `, ${data.failed} failed` : ""}`;
      if (data.failed) toast.warning(msg + (data.errors?.length ? `: ${data.errors.slice(0,3).join("; ")}` : ""));
      else toast.success(msg);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const repRoleSyncAll = async () => {
    if (!window.confirm(`Re-assign the Discord rep role to every linked rep on all ${approvedTeams.length} teams? Skips teams without a rep role or without any linked reps.`)) return;
    try {
      const { data } = await api.post("/admin/teams/rep-role-sync-all");
      const parts = [
        `${data.assigned} rep${data.assigned === 1 ? "" : "s"} assigned across ${data.teams_processed}/${data.total_teams} teams`,
      ];
      if (data.teams_without_rep_role) parts.push(`${data.teams_without_rep_role} team(s) still need Discord sync`);
      const msg = "Rep role sync · " + parts.join(" · ");
      if (data.errors?.length) toast.warning(msg + " · " + data.errors.slice(0, 3).join("; "));
      else toast.success(msg);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleVerify = async (s) => {
    try { const { data } = await api.post(`/admin/streamers/${s.id}/verify`); toast.success(data.verified ? "Streamer verified." : "Verification removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteStreamer = async (s) => {
    if (!window.confirm(`Delete streamer ${s.handle}?`)) return;
    try { await api.delete(`/admin/streamers/${s.id}`); toast.success("Streamer removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const changeUserRole = async (u, role) => {
    try { await api.post(`/admin/users/${u.id}/role`, { role }); toast.success(`${u.email} → ${role.toUpperCase()}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This wipes their teams, invites, predictions and watchlist.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Account removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createDivision = async () => {
    if (!divForm.name.trim()) { toast.error("Division name required."); return; }
    try {
      await api.post("/admin/divisions", divForm);
      toast.success(`Division ${divForm.name} created.`);
      setDivForm({ name: "", tier: "Mains", color: "#00F0FF", sort_order: 0, description: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteDivision = async (d) => {
    if (!window.confirm(`Delete division ${d.name}? Teams will be unassigned.`)) return;
    try { await api.delete(`/admin/divisions/${d.id}`); toast.success("Division removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const assignTeam = async () => {
    if (!assignTargetDiv || !assignTeamId) return;
    try {
      const { data } = await api.post(`/admin/divisions/${assignTargetDiv.id}/assign`, { team_ids: [assignTeamId] });
      const moved = (data?.moved_from || []).find((m) => m.team_id === assignTeamId);
      if (moved?.from_division) {
        toast.success(`Moved from ${moved.from_division}${moved.from_tier && moved.from_tier !== data.to_tier ? ` (${moved.from_tier})` : ""} → ${data.to_division}${data.to_tier ? ` (${data.to_tier})` : ""}`);
      } else {
        toast.success(`Team assigned to ${assignTargetDiv.name}.`);
      }
      setAssignTargetDiv(null);
      setAssignTeamId("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const confirmMove = async () => {
    if (!moveTarget || !moveDestDivId) return;
    try {
      const { data } = await api.post(`/admin/divisions/${moveDestDivId}/assign`, { team_ids: [moveTarget.id] });
      toast.success(`${moveTarget.tag} moved → ${data.to_division}${data.to_tier ? ` (${data.to_tier})` : ""}`);
      setMoveTarget(null);
      setMoveDestDivId("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const unassignTeam = async (d, teamId) => {
    try {
      await api.post(`/admin/divisions/${d.id}/unassign`, { team_ids: [teamId] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createSponsor = async () => {
    if (!sponsorForm.title.trim()) { toast.error("Title required."); return; }
    if (!sponsorForm.url.trim()) { toast.error("URL required."); return; }
    if (!sponsorForm.file) { toast.error("Logo image required."); return; }
    const fd = new FormData();
    fd.append("title", sponsorForm.title.trim());
    fd.append("url", sponsorForm.url.trim());
    fd.append("sort_order", String(sponsorForm.sort_order || 0));
    fd.append("file", sponsorForm.file);
    try {
      await apiUpload("/admin/sponsors", fd);
      toast.success(`Sponsor "${sponsorForm.title}" added.`);
      setSponsorForm({ title: "", url: "", sort_order: 0, file: null });
      if (sponsorFileRef.current) sponsorFileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const updateSponsorOrder = async (s, delta) => {
    const fd = new FormData();
    fd.append("sort_order", String((s.sort_order || 0) + delta));
    try {
      await apiUpload(`/admin/sponsors/${s.id}`, fd);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteSponsor = async (s) => {
    if (!window.confirm(`Remove sponsor "${s.title}"?`)) return;
    try {
      await api.delete(`/admin/sponsors/${s.id}`);
      toast.success("Sponsor removed.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-root">
      <div>
        <div className="text-[#FF007F] font-mono tracking-[0.3em] text-xs mb-2 flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" /> / COMMAND CENTER · HLL
        </div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Admin Panel</h1>
        <p className="text-gray-400 mt-2 text-sm">
          Applications, teams, transfers, rosters, streamer verification, member roles — all here.
        </p>
      </div>

      <VerifyTicker limit={5} intervalMs={15000} />

      {/* Live sync status */}
      {syncStatus && (
        <div
          data-testid="admin-live-sync-widget"
          className="border border-[#232330] bg-[#101015] p-4 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <Radio className="w-4 h-4 text-[#B084FF]" />
            <div>
              <div className="text-xs font-heading font-black uppercase tracking-tight">Auto Live Sync</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                {syncStatus.twitch_configured || syncStatus.youtube_configured
                  ? `Polling every ${syncStatus.interval_seconds}s`
                  : "No Twitch or YouTube keys configured — sync idle"}
              </div>
            </div>
            <span
              data-testid="admin-sync-twitch-status"
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${
                syncStatus.twitch_configured ? "border-[#a970ff] text-[#a970ff]" : "border-[#232330] text-gray-600"
              }`}
            >
              Twitch · {syncStatus.twitch_configured ? "on" : "off"}
            </span>
            <span
              data-testid="admin-sync-youtube-status"
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${
                syncStatus.youtube_configured ? "border-red-500 text-red-500" : "border-[#232330] text-gray-600"
              }`}
            >
              YouTube · {syncStatus.youtube_configured ? "on" : "off"}
            </span>
          </div>
          <Button
            onClick={runSync}
            disabled={syncing || (!syncStatus.twitch_configured && !syncStatus.youtube_configured)}
            data-testid="admin-sync-now"
            className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync live now"}
          </Button>
        </div>
      )}

      {/* Discord webhook widget */}
      <div
        data-testid="admin-discord-widget"
        className="border border-[#232330] bg-[#101015] p-4 space-y-3"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <MessageSquare className="w-4 h-4 text-[#5865F2]" />
          <div>
            <div className="text-xs font-heading font-black uppercase tracking-tight">Discord broadcast</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
              Push standings, results, live streams and more into your server. Auto-alerts fire on match live + full-time.
            </div>
          </div>
          <span
            data-testid="admin-discord-status"
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ml-auto ${
              discord.configured ? "border-emerald-400 text-emerald-400" : "border-[#232330] text-gray-600"
            }`}
          >
            {discord.configured ? `Webhook · ${discord.url_preview || "channels only"}` : "Webhook · not set"}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={discordDraft}
            onChange={(e) => setDiscordDraft(e.target.value)}
            data-testid="admin-discord-url"
            placeholder={discord.url_preview ? "Paste a new default webhook, or leave blank to keep…" : "Default (fallback) webhook · https://discord.com/api/webhooks/…"}
            className="rounded-none bg-[#05050A] border-[#232330] font-mono text-xs flex-1 min-w-[240px]"
          />
          <Button
            onClick={() => setShowDiscordChannels((v) => !v)}
            data-testid="admin-discord-channels-toggle"
            variant="outline"
            className="rounded-none border-[#5865F2]/60 text-[#5865F2] hover:bg-[#5865F2] hover:text-white uppercase tracking-widest text-xs font-bold"
          >
            {showDiscordChannels ? "Hide" : "Per-channel"} URLs
          </Button>
          <Button
            onClick={saveDiscordWebhook}
            data-testid="admin-discord-save"
            className="rounded-none bg-[#5865F2] hover:bg-indigo-400 text-white uppercase tracking-widest text-xs font-bold"
          >
            Save webhook
          </Button>
        </div>
        {showDiscordChannels && (
          <div data-testid="admin-discord-channels" className="grid gap-2 md:grid-cols-2 pt-1">
            {[
              ["live", "Live alerts"],
              ["results", "Results feed"],
              ["standings", "Standings"],
              ["predictions", "Predictions leaderboard"],
              ["schedule", "Schedule + new rounds"],
            ].map(([k, label]) => (
              <div key={k} className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</span>
                  <span className={`text-[9px] font-mono uppercase tracking-widest ${discord.channels?.[k]?.configured ? "text-emerald-400" : "text-gray-600"}`}>
                    {discord.channels?.[k]?.configured ? "SET" : "unset"}
                  </span>
                </div>
                <Input
                  value={discordChannels[k]}
                  onChange={(e) => setDiscordChannels((c) => ({ ...c, [k]: e.target.value }))}
                  data-testid={`admin-discord-ch-${k}`}
                  placeholder={discord.channels?.[k]?.configured ? "Replace URL, or leave blank to keep…" : "https://discord.com/api/webhooks/…"}
                  className="rounded-none bg-[#05050A] border-[#232330] font-mono text-[11px]"
                />
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            onClick={() => postDiscord("leaderboard")}
            disabled={!discord.configured}
            data-testid="admin-discord-post-leaderboard"
            className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            <Trophy className="w-3.5 h-3.5 mr-2" /> Post leaderboard
          </Button>
          <Button
            onClick={() => postDiscord("weekly-top-predictors")}
            disabled={!discord.configured}
            data-testid="admin-discord-post-weekly-top-predictors"
            className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            <Trophy className="w-3.5 h-3.5 mr-2" /> Post weekly top
          </Button>
          <Button
            onClick={() => postDiscord("schedule")}
            disabled={!discord.configured}
            data-testid="admin-discord-post-schedule"
            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            <Calendar className="w-3.5 h-3.5 mr-2" /> Post schedule
          </Button>
          <Button
            onClick={() => postDiscord("standings")}
            disabled={!discord.configured}
            data-testid="admin-discord-post-standings"
            className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            <Trophy className="w-3.5 h-3.5 mr-2" /> Post standings
          </Button>
          <Button
            onClick={() => postDiscord("results")}
            disabled={!discord.configured}
            data-testid="admin-discord-post-results"
            className="rounded-none bg-emerald-400 hover:bg-emerald-300 text-black uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            <Trophy className="w-3.5 h-3.5 mr-2" /> Post results
          </Button>
          <Button
            onClick={() => postDiscord("live-streams")}
            disabled={!discord.configured}
            data-testid="admin-discord-post-live-streams"
            className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            <MessageSquare className="w-3.5 h-3.5 mr-2" /> Post live streams
          </Button>
        </div>
      </div>

      {/* Iter44 · Season countdown banner (homepage) */}
      <div
        data-testid="admin-season-widget"
        className="border border-[#232330] bg-[#101015] p-4 space-y-3"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <Lock className="w-4 h-4 text-[#F4D03F]" />
          <div>
            <div className="text-xs font-heading font-black uppercase tracking-tight">Season countdown</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
              Ticks down on the homepage so teams trim their rosters before lockdown. Clear the date to hide the banner.
            </div>
          </div>
          <span
            data-testid="admin-season-status"
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ml-auto ${
              season?.target_at ? "border-[#F4D03F] text-[#F4D03F]" : "border-[#232330] text-gray-600"
            }`}
          >
            {season?.target_at ? `LIVE · ${new Date(season.target_at).toLocaleString()}` : "OFF"}
          </span>
        </div>
        <div className="grid gap-2 md:grid-cols-3">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Lock at (local time)</span>
            <Input
              type="datetime-local"
              value={seasonDraft.target_at ? new Date(seasonDraft.target_at).toISOString().slice(0, 16) : ""}
              onChange={(e) => setSeasonDraft((s) => ({ ...s, target_at: e.target.value }))}
              data-testid="admin-season-date"
              className="rounded-none bg-[#05050A] border-[#232330] font-mono text-xs"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Label</span>
            <Input
              value={seasonDraft.label}
              onChange={(e) => setSeasonDraft((s) => ({ ...s, label: e.target.value }))}
              data-testid="admin-season-label"
              placeholder="Season lockdown"
              className="rounded-none bg-[#05050A] border-[#232330] font-mono text-xs"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Message</span>
            <Input
              value={seasonDraft.message}
              onChange={(e) => setSeasonDraft((s) => ({ ...s, message: e.target.value }))}
              data-testid="admin-season-message"
              placeholder="Trim your active roster before the whistle…"
              className="rounded-none bg-[#05050A] border-[#232330] font-mono text-xs"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={saveSeasonCountdown}
            data-testid="admin-season-save"
            className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold"
          >
            <Timer className="w-3.5 h-3.5 mr-2" /> Save countdown
          </Button>
          {season?.target_at && (
            <Button
              onClick={() => { setSeasonDraft({ target_at: "", label: "", message: "" }); }}
              data-testid="admin-season-clear-draft"
              variant="outline"
              className="rounded-none border-[#232330] hover:border-white uppercase tracking-widest text-xs font-bold"
            >
              Clear draft
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#232330] overflow-x-auto">
        {TABS.filter((t) => isAdmin || t.officialAllowed).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-colors ${
              tab === t.key ? "text-[#FF007F] border-b-2 border-[#FF007F] -mb-px" : "text-gray-400 hover:text-white"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
            {t.key === "apps" && pending.length > 0 && (
              <span className="ml-1 bg-[#FF007F] text-white text-[9px] font-mono px-1.5 py-0.5">{pending.length}</span>
            )}
          </button>
        ))}
        {!isAdmin && isOfficial && (
          <div className="ml-auto text-[9px] font-mono uppercase tracking-widest text-gray-500 self-center pr-3" data-testid="official-badge">
            League Office · roster-only access
          </div>
        )}
      </div>

      {tab === "apps" && (
        <section className="space-y-3" data-testid="admin-apps">
          {pendingApproval.length > 0 && (
            <div className="border border-[#FF007F]/40 bg-[#FF007F]/5 p-5" data-testid="admin-pending-approval">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF007F] mb-3 flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5" /> Results awaiting approval ({pendingApproval.length})
              </div>
              <div className="space-y-2">
                {pendingApproval.map((f) => {
                  const home = teams.find((t) => t.id === f.home_team_id) || {};
                  const away = teams.find((t) => t.id === f.away_team_id) || {};
                  const pr = f.proposed_result || {};
                  return (
                    <div
                      key={f.id}
                      data-testid={`pending-approval-${f.id}`}
                      className="flex flex-wrap items-center justify-between gap-3 border border-[#232330] bg-[#101015] p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-black text-base tracking-tight uppercase truncate">
                          {home.tag || "?"} <span className="text-[#FF007F] font-mono text-sm">{pr.home_score}-{pr.away_score}</span> {away.tag || "?"}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                          Both reps signed · submitted by {pr.submitter_email || "rep"}
                          {f.pending_approval_at && (
                            <> · {new Date(f.pending_approval_at).toLocaleString()}</>
                          )}
                        </div>
                        {f.pending_approval_at && (() => {
                          const expiresAt = new Date(f.pending_approval_at).getTime() + 24 * 60 * 60 * 1000;
                          const remainingMs = expiresAt - Date.now();
                          const hrs = Math.max(0, Math.floor(remainingMs / 3_600_000));
                          const mins = Math.max(0, Math.floor((remainingMs % 3_600_000) / 60_000));
                          const expired = remainingMs <= 0;
                          return (
                            <div
                              className={`inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 border text-[9px] font-mono uppercase tracking-widest ${
                                expired
                                  ? "border-[#00FFB2]/60 bg-[#00FFB2]/10 text-[#00FFB2]"
                                  : "border-[#F4D03F]/40 bg-[#F4D03F]/5 text-[#F4D03F]"
                              }`}
                              data-testid={`pending-approval-countdown-${f.id}`}
                            >
                              {expired
                                ? "⏱ Eligible for auto-approve"
                                : `⏱ Auto-approves in ${hrs}h ${mins}m`}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => approveResult(f)}
                          data-testid={`pending-approval-approve-${f.id}`}
                          className="rounded-none bg-[#00FFB2] hover:bg-emerald-300 text-black uppercase tracking-widest text-xs font-bold h-9"
                        >
                          <Check className="w-3.5 h-3.5 mr-1.5" /> Approve
                        </Button>
                        <Button
                          onClick={() => rejectResult(f)}
                          data-testid={`pending-approval-reject-${f.id}`}
                          variant="outline"
                          className="rounded-none border-red-500 text-red-400 hover:bg-red-500 hover:text-white uppercase tracking-widest text-xs font-bold h-9"
                        >
                          <X className="w-3.5 h-3.5 mr-1.5" /> Reject
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {pending.length === 0 && (
            <div className="border border-[#232330] p-10 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
              No pending team applications.
            </div>
          )}
          {pending.map((t) => (
            <div key={t.id} data-testid={`app-${t.tag}`} className="border border-[#F4D03F]/40 bg-[#F4D03F]/5 p-5 flex flex-wrap items-center justify-between gap-4" style={{ borderLeft: `4px solid ${t.logo_color}` }}>
              <div>
                <div className="text-[10px] font-mono tracking-widest text-[#F4D03F] uppercase mb-1">
                  PENDING · {t.tier || "Mains"} tier · roster cap {t.roster_cap || 150} · applied {new Date(t.applied_at || t.created_at).toLocaleDateString()}
                </div>
                <div className="font-heading text-2xl font-black uppercase tracking-tight" style={{ color: t.logo_color }}>
                  {t.name} <span className="text-gray-500 text-base ml-2">{t.tag}</span>
                </div>
                <div className="text-xs text-gray-400 font-mono mt-1">
                  Manager: {t.manager} · {t.owner_email || "—"}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => approve(t.id)} data-testid={`approve-${t.tag}`}
                        className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold">
                  <Check className="w-3.5 h-3.5 mr-2" /> Approve
                </Button>
                <Button onClick={() => { setDeclineFor(t); setDeclineReason(""); }} data-testid={`decline-${t.tag}`}
                        variant="outline"
                        className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-xs">
                  <X className="w-3.5 h-3.5 mr-2" /> Decline
                </Button>
              </div>
            </div>
          ))}
        </section>
      )}

      {tab === "teams" && (
        <AdminTeamsTable
          approvedTeams={approvedTeams}
          declinedTeams={declinedTeams}
          players={players}
          onRecomputeStandings={recomputeStandings}
          onDiscordSyncAll={discordSyncAll}
          onRepRoleSyncAll={repRoleSyncAll}
          onLockAll={lockAllRosters}
          onUnlockAll={unlockAllRosters}
          onVerifyAllTeams={verifyAllTeams}
          onDiscordSyncTeam={discordSyncTeam}
          onSetRepRole={setDiscordRepRole}
          onSwitchTier={switchTier}
          onToggleRosterLock={toggleRosterLock}
          onVerifyAllPlayers={verifyAllTeam}
          onDelete={deleteTeam}
        />
      )}
      {tab === "rosters" && (
        <section className="space-y-2" data-testid="admin-rosters">
          <div className="flex items-center justify-between gap-3 flex-wrap border border-[#232330] bg-[#101015] p-4" data-testid="admin-rosters-toolbar">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]">/ Season Archive</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Export every approved team's CVC into one zip — perfect for end-of-season snapshots.
              </div>
            </div>
            <Button
              onClick={exportAllRosters}
              disabled={bulkExportBusy || approvedTeams.length === 0}
              data-testid="admin-rosters-export-all"
              className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold"
            >
              <Download className="w-3.5 h-3.5 mr-2" />
              {bulkExportBusy ? "Zipping…" : `Export all rosters (${approvedTeams.length})`}
            </Button>
          </div>
          {approvedTeams.map((t) => {
            const roster = players.filter((p) => p.team_id === t.id);
            return (
              <div key={t.id} data-testid={`roster-row-${t.tag}`} className="border border-[#232330] bg-[#101015] p-4" style={{ borderLeft: `4px solid ${t.logo_color}` }}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-heading font-black text-lg uppercase tracking-tight" style={{ color: t.logo_color }}>{t.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{roster.length}/{t.roster_cap || 150} players · {t.tier || "Mains"}</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => exportTeamRoster(t)} data-testid={`export-roster-${t.tag}`}
                            variant="outline"
                            className="rounded-none border-[#232330] hover:border-[#F4D03F] hover:text-[#F4D03F] uppercase tracking-widest text-xs">
                      <Download className="w-3.5 h-3.5 mr-2" /> Export CVC
                    </Button>
                    <label
                      data-testid={`replace-roster-${t.tag}`}
                      className="h-9 inline-flex items-center cursor-pointer rounded-none border border-[#232330] hover:border-[#B084FF] hover:text-[#B084FF] text-xs uppercase tracking-widest px-3 font-bold"
                    >
                      <Upload className="w-3.5 h-3.5 mr-2" /> Replace file
                      <input
                        type="file"
                        accept=".csv,.cvc,.pdf,text/csv,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          replaceTeamRoster(t, f);
                          e.target.value = ""; // reset so re-selecting the same file still fires onChange
                        }}
                      />
                    </label>
                    <Button size="sm" onClick={() => openVerification(t)} data-testid={`verify-roster-${t.tag}`}
                            variant="outline"
                            className="rounded-none border-[#232330] hover:border-cyan-400 hover:text-cyan-400 uppercase tracking-widest text-xs">
                      <BadgeCheck className="w-3.5 h-3.5 mr-2" /> HLL verify
                    </Button>
                    <Button size="sm" onClick={() => openRosterEditor(t)} data-testid={`edit-roster-${t.tag}`}
                            className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-xs font-bold">
                      <Pencil className="w-3.5 h-3.5 mr-2" /> Edit roster
                    </Button>
                  </div>
                </div>
                {roster.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {roster.slice(0, 40).map((p) => (
                      <span key={p.id} className="text-xs font-mono px-2 py-1 border border-[#232330] flex items-center gap-1">
                        {p.hll_verified && <BadgeCheck className="w-3 h-3 text-cyan-400" />}
                        {p.handle}{p.player_id ? <span className="text-gray-500"> · {p.player_id}</span> : null}
                      </span>
                    ))}
                    {roster.length > 40 && (
                      <span className="text-xs font-mono px-2 py-1 border border-[#232330] text-gray-500">+{roster.length - 40} more</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {approvedTeams.length === 0 && (
            <div className="border border-[#232330] p-8 text-center text-gray-500 font-mono text-xs">Approve a team first to edit rosters.</div>
          )}
        </section>
      )}

      {tab === "transfers" && (
        <section data-testid="admin-transfers">
          <div className="border border-[#232330] overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="border-b border-[#232330] bg-[#0a0a12]">
                  {["Date", "Player", "From", "To", "Fee", "Status", ""].map((h) => (
                    <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-gray-500 px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transfers.map((t) => (
                  <tr key={t.id} data-testid={`admin-transfer-${t.id}`} className="border-b border-[#232330]">
                    <td className="px-4 py-3 font-mono text-gray-500 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 font-heading font-bold uppercase tracking-tight">{playerById(t.player_id)?.handle || "—"}</td>
                    <td className="px-4 py-3 text-gray-400">{teamById(t.from_team_id)?.tag || "FA"}</td>
                    <td className="px-4 py-3 text-white">{teamById(t.to_team_id)?.tag || "—"}</td>
                    <td className="px-4 py-3 font-mono text-[#F4D03F]">{t.fee}</td>
                    <td className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-400">{t.status}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => deleteTransfer(t)} data-testid={`delete-transfer-${t.id}`}
                              className="text-gray-500 hover:text-red-500" aria-label="Delete transfer">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {transfers.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No transfers logged.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "streamers" && (
        <section className="space-y-2" data-testid="admin-streamers">
          {streamers.map((s) => (
            <div key={s.id} data-testid={`streamer-row-${s.handle}`}
                 className="border border-[#232330] bg-[#101015] p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {s.avatar_url && <img src={s.avatar_url} alt={s.handle} className="w-10 h-10 object-cover border border-[#232330]" />}
                <div>
                  <div className="font-heading font-black text-lg uppercase tracking-tight flex items-center gap-2">
                    {s.handle}
                    {s.verified && <BadgeCheck className="w-4 h-4 text-cyan-400" />}
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    {s.platform} · {s.country} · {s.email || "no email"}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => toggleVerify(s)} data-testid={`verify-${s.handle}`}
                        className={`rounded-none uppercase tracking-widest text-xs font-bold ${
                          s.verified
                            ? "bg-transparent border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10"
                            : "bg-cyan-400 hover:bg-cyan-300 text-black"
                        }`}>
                  <BadgeCheck className="w-3.5 h-3.5 mr-2" /> {s.verified ? "Unverify" : "Verify"}
                </Button>
                <Button size="sm" onClick={() => deleteStreamer(s)} data-testid={`delete-streamer-${s.handle}`}
                        variant="outline"
                        className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-xs">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {streamers.length === 0 && (
            <div className="border border-[#232330] p-8 text-center text-gray-500 font-mono text-xs">No streamer dossiers yet.</div>
          )}
        </section>
      )}

      {tab === "sponsors" && (
        <AdminSponsors
          sponsors={sponsors}
          form={sponsorForm}
          setForm={setSponsorForm}
          fileRef={sponsorFileRef}
          onCreate={createSponsor}
          onDelete={deleteSponsor}
          onReorder={updateSponsorOrder}
        />
      )}
      {tab === "discord" && (
        <AdminDiscordHealth
          health={discordHealth}
          onLoad={loadDiscordHealth}
          onResyncTeam={discordSyncTeam}
        />
      )}
      {tab === "users" && (
        <section data-testid="admin-users">
          <div className="border border-[#232330] overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="border-b border-[#232330] bg-[#0a0a12]">
                  {["Email", "Name", "Role", "Joined", ""].map((h) => (
                    <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-gray-500 px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-[#232330]">
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">{u.name || "—"}</td>
                    <td className="px-4 py-3">
                      <Select value={u.role || "member"} onValueChange={(v) => changeUserRole(u, v)}>
                        <SelectTrigger data-testid={`role-${u.email}`}
                                       className={`rounded-none bg-transparent border ${ROLE_COLORS[u.role || "member"]} font-mono uppercase text-xs w-36`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#101015] border-[#232330] text-white">
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="rep">Clan Rep</SelectItem>
                          <SelectItem value="streamer">Streamer</SelectItem>
                          <SelectItem value="official">League Official</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-500 text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.id !== user.id && (
                        <button onClick={() => deleteUser(u)} data-testid={`delete-user-${u.email}`}
                                className="text-gray-500 hover:text-red-500" aria-label="Delete user">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No users.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "rounds" && (
        <section className="space-y-6" data-testid="admin-rounds">
          <div className="border border-[#232330] bg-[#101015] p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] mb-3 flex items-center gap-2">
              <Swords className="w-3.5 h-3.5" /> Create round
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-3">
              <div className="space-y-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Round name (optional)</Label>
                  <Input
                    data-testid="round-name"
                    value={roundName}
                    onChange={(e) => setRoundName(e.target.value)}
                    placeholder={`Round ${rounds.length + 1}`}
                    className="rounded-none bg-[#05050A] border-[#232330] font-mono"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Weekend (any date Fri–Sun UTC)</Label>
                  <Input
                    data-testid="round-weekend"
                    type="datetime-local"
                    value={roundWeekend}
                    onChange={(e) => setRoundWeekend(e.target.value)}
                    className="rounded-none bg-[#05050A] border-[#232330] font-mono"
                  />
                  <div className="text-[9px] font-mono uppercase tracking-widest text-gray-600 mt-1">
                    Auto-snaps to the Friday of that ISO week. One game per team.
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">Matchups</Label>
                  <button
                    onClick={() => setRoundMatchups([...roundMatchups, { home_team_id: "", away_team_id: "" }])}
                    data-testid="round-add-matchup"
                    className="text-[9px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400/40 hover:border-cyan-400 px-2 py-1 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add matchup
                  </button>
                </div>
                {roundMatchups.map((m, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
                    <select
                      value={m.home_team_id}
                      onChange={(e) => {
                        const next = [...roundMatchups];
                        next[i] = { ...next[i], home_team_id: e.target.value };
                        setRoundMatchups(next);
                      }}
                      data-testid={`round-home-${i}`}
                      className="rounded-none bg-[#05050A] border border-[#232330] p-2 font-mono text-xs"
                    >
                      <option value="">Home team…</option>
                      {approvedTeams.map((t) => <option key={t.id} value={t.id}>{t.tag} · {t.name}</option>)}
                    </select>
                    <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">vs</span>
                    <select
                      value={m.away_team_id}
                      onChange={(e) => {
                        const next = [...roundMatchups];
                        next[i] = { ...next[i], away_team_id: e.target.value };
                        setRoundMatchups(next);
                      }}
                      data-testid={`round-away-${i}`}
                      className="rounded-none bg-[#05050A] border border-[#232330] p-2 font-mono text-xs"
                    >
                      <option value="">Away team…</option>
                      {approvedTeams.map((t) => <option key={t.id} value={t.id}>{t.tag} · {t.name}</option>)}
                    </select>
                    <button
                      onClick={() => setRoundMatchups(roundMatchups.filter((_, idx) => idx !== i))}
                      disabled={roundMatchups.length === 1}
                      data-testid={`round-remove-matchup-${i}`}
                      className="text-gray-500 hover:text-red-500 disabled:opacity-30"
                      aria-label="Remove matchup"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <Button
                  onClick={createRound}
                  data-testid="round-create"
                  className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold w-full mt-2"
                >
                  <Send className="w-3.5 h-3.5 mr-2" /> Publish round · alert reps
                </Button>
              </div>
            </div>
          </div>

          <div className="border border-[#232330] bg-[#101015] p-5" data-testid="admin-repost-slates">
            <div className="text-[10px] font-mono uppercase tracking-widest text-purple-400 mb-3 flex items-center gap-2">
              <Send className="w-3.5 h-3.5" /> Repost season slates to team chats
            </div>
            <div className="text-[10px] font-mono text-gray-500 mb-3 leading-relaxed">
              Push every approved team's full season slate into their private Discord team channel. Use after a rebuild, mid-season reschedule, or when new team channels were provisioned after the initial draw.
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={repostTier}
                onChange={(e) => setRepostTier(e.target.value)}
                data-testid="repost-tier"
                className="rounded-none bg-[#05050A] border border-[#232330] p-2 font-mono text-xs h-9"
              >
                <option value="">All divisions</option>
                <option value="Lite">Lite tier only</option>
                <option value="Mains">Mains tier only</option>
              </select>
              <Button
                onClick={repostTeamSlates}
                disabled={reposting}
                data-testid="repost-run"
                className="rounded-none bg-purple-500 hover:bg-purple-400 text-white uppercase tracking-widest text-xs font-bold h-9 disabled:opacity-40"
              >
                <Send className="w-3.5 h-3.5 mr-2" /> {reposting ? "Reposting…" : "Repost to team chats"}
              </Button>
            </div>
            {repostResult && (
              <div data-testid="repost-result" className="mt-3 text-[11px] font-mono text-gray-300 border-t border-[#232330] pt-3 space-y-1">
                <div>
                  Posted to <span className="text-purple-300 font-bold">{repostResult.posted}</span> team channel{repostResult.posted === 1 ? "" : "s"} ·
                  {" "}Skipped <span className="text-gray-400 font-bold">{repostResult.skipped}</span>
                  {repostResult.total_teams ? <> / {repostResult.total_teams} teams scanned</> : null}
                </div>
                {repostResult.errors?.length > 0 && (
                  <div className="text-red-400">Errors: {repostResult.errors.slice(0, 3).join(" · ")}</div>
                )}
              </div>
            )}
          </div>

          <div className="border border-[#232330] bg-[#101015] p-5" data-testid="admin-fixture-dedupe">
            <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 mb-3 flex items-center gap-2">
              <Filter className="w-3.5 h-3.5" /> Dedupe duplicate fixtures
            </div>
            <div className="text-[10px] font-mono text-gray-500 mb-3 leading-relaxed">
              Scans rounds R2–R11 for pairs of teams accidentally scheduled twice in the same round (e.g., re-running Season Generator without a wipe). Dry-run first — click Apply to delete the extras. Never touches R1.
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Rounds</div>
              <Input
                type="number" min={1}
                value={dedupeFrom}
                onChange={(e) => setDedupeFrom(e.target.value)}
                data-testid="dedupe-from-round"
                className="w-16 rounded-none bg-[#05050A] border-[#232330] font-mono text-xs h-9"
                placeholder="2"
              />
              <span className="text-gray-500">→</span>
              <Input
                type="number" min={1}
                value={dedupeTo}
                onChange={(e) => setDedupeTo(e.target.value)}
                data-testid="dedupe-to-round"
                className="w-16 rounded-none bg-[#05050A] border-[#232330] font-mono text-xs h-9"
                placeholder="11"
              />
              <Button
                onClick={() => runDedupe(false)}
                data-testid="dedupe-scan"
                variant="outline"
                className="rounded-none border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black uppercase tracking-widest text-xs font-bold h-9"
              >
                Dry-run scan
              </Button>
              <Button
                onClick={() => runDedupe(true)}
                data-testid="dedupe-apply"
                className="rounded-none bg-red-500 hover:bg-red-400 text-white uppercase tracking-widest text-xs font-bold h-9"
              >
                Apply · delete extras
              </Button>
            </div>
            {dedupeResult && (
              <div data-testid="dedupe-result" className="mt-3 text-[11px] font-mono text-gray-300 border-t border-[#232330] pt-3 space-y-1">
                <div>
                  Scanned <span className="text-[#F4D03F] font-bold">{dedupeResult.scanned}</span> fixtures ·
                  {" "}Found <span className="text-cyan-400 font-bold">{dedupeResult.duplicate_groups}</span> duplicate group{dedupeResult.duplicate_groups === 1 ? "" : "s"} ·
                  {dedupeResult.applied
                    ? <> Deleted <span className="text-red-400 font-bold">{dedupeResult.deleted}</span> extras</>
                    : <> Would delete <span className="text-orange-400 font-bold">{dedupeResult.would_delete}</span> extras</>}
                </div>
                {dedupeResult.details?.slice(0, 8).map((d, i) => (
                  <div key={i} className="text-gray-500 pl-2">
                    · R{d.round_number} · {d.pair} · {d.total_rows} rows → keep 1, drop {d.would_delete_ids?.length || 0}
                  </div>
                ))}
                {dedupeResult.details && dedupeResult.details.length > 8 && (
                  <div className="text-gray-600 pl-2">…{dedupeResult.details.length - 8} more</div>
                )}
              </div>
            )}
          </div>

          <div className="border border-[#232330] bg-[#101015] p-5" data-testid="admin-roster-dupes">
            <div className="text-[10px] font-mono uppercase tracking-widest text-pink-400 mb-3 flex items-center gap-2">
              <Filter className="w-3.5 h-3.5" /> Roster duplicate audit
            </div>
            <div className="text-[10px] font-mono text-gray-500 mb-3 leading-relaxed">
              Scans every rostered player and flags anyone registered to more than one team (by handle OR HLL records identity), plus duplicate handles inside the same team. Read-only — review each group then fix via the Teams panel.
            </div>
            <Button
              onClick={runRosterDupes}
              disabled={rosterDupesBusy}
              data-testid="roster-dupes-scan"
              variant="outline"
              className="rounded-none border-pink-400 text-pink-400 hover:bg-pink-400 hover:text-black uppercase tracking-widest text-xs font-bold h-9"
            >
              {rosterDupesBusy ? "Scanning…" : "Run roster audit"}
            </Button>
            {rosterDupes && (
              <div data-testid="roster-dupes-result" className="mt-4 text-[11px] font-mono text-gray-300 border-t border-[#232330] pt-3 space-y-4">
                <div>
                  Scanned <span className="text-[#F4D03F] font-bold">{rosterDupes.scanned_players}</span> players across{" "}
                  <span className="text-[#F4D03F] font-bold">{rosterDupes.scanned_teams}</span> teams.
                </div>
                {[
                  ["cross_team_handle", "Same handle on multiple teams", "#FF007F"],
                  ["cross_team_hll", "Same HLL records identity on multiple teams", "#F4D03F"],
                  ["intra_team_handle", "Duplicate handle inside one team", "#B084FF"],
                ].map(([key, label, color]) => {
                  const groups = rosterDupes[key] || [];
                  return (
                    <div key={key} data-testid={`roster-dupes-group-${key}`} className="space-y-2">
                      <div className="uppercase tracking-widest text-[10px]" style={{ color }}>
                        {label} <span className="text-gray-500">· {groups.length}</span>
                      </div>
                      {groups.length === 0 && (
                        <div className="text-gray-600 pl-2">— clean —</div>
                      )}
                      {groups.map((g, i) => (
                        <div key={i} className="border border-[#232330] bg-[#05050A] p-2 space-y-1">
                          <div className="flex flex-wrap items-center gap-2 text-[10px]">
                            {key === "intra_team_handle" ? (
                              <>
                                <span className="text-gray-500">Team</span>
                                <span className="px-1.5 py-0.5 bg-[#101015] border border-[#232330]" style={{ color: g.team_color || "#fff" }}>
                                  {g.team_tag}
                                </span>
                                <span className="text-white font-bold">{g.handle}</span>
                                <span className="text-gray-500">· {g.count} entries</span>
                              </>
                            ) : (
                              <>
                                <span className="text-gray-500">Identity</span>
                                <span className="text-white font-bold">{g.key}</span>
                                <span className="text-gray-500">· {g.team_count} teams · {g.count} rows</span>
                              </>
                            )}
                          </div>
                          <div className="grid gap-1 pl-2">
                            {(g.players || []).map((p) => (
                              <div
                                key={p.player_id}
                                data-testid={`roster-dupes-player-${p.player_id}`}
                                className="flex flex-wrap items-center gap-2 text-[10px]"
                              >
                                <span className="px-1.5 py-0.5 bg-[#101015] border border-[#232330]" style={{ color: p.team_color || "#fff" }}>
                                  {p.team_tag}
                                </span>
                                <span className="text-white">{p.handle}</span>
                                {p.hll_verified && <span className="text-[#00FFB2]">✓ verified</span>}
                                {p.hll_url && (
                                  <a
                                    href={p.hll_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-cyan-400 hover:underline truncate max-w-[220px]"
                                  >
                                    hllrecords
                                  </a>
                                )}
                                <a
                                  href={`/player-id/${encodeURIComponent(p.player_id || "")}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-gray-500 hover:text-gray-300 underline"
                                >
                                  career card
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border border-[#232330] bg-[#101015] p-5" data-testid="admin-round-reschedule">
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-400 mb-3 flex items-center gap-2">
              <Send className="w-3.5 h-3.5" /> Bulk reschedule a round
            </div>
            <div className="text-[10px] font-mono text-gray-500 mb-3 leading-relaxed">
              Push every fixture in a given round into a new date window. Kickoff time is preserved; fixtures distribute Fri/Sat/Sun across the window. If a match thread exists, an admin-reschedule notice is dropped inside it.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_140px_140px_auto] gap-2 items-end">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Round #</Label>
                <Input
                  type="number"
                  min="1"
                  value={reschedRound}
                  onChange={(e) => setReschedRound(e.target.value)}
                  data-testid="reschedule-round-number"
                  placeholder="12"
                  className="rounded-none bg-[#05050A] border-[#232330] font-mono"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Tier (optional)</Label>
                <select
                  value={reschedTier}
                  onChange={(e) => setReschedTier(e.target.value)}
                  data-testid="reschedule-tier"
                  className="w-full rounded-none bg-[#05050A] border border-[#232330] p-2 font-mono text-xs h-10"
                >
                  <option value="">Both tiers</option>
                  <option value="Lite">Lite</option>
                  <option value="Mains">Mains</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Start date</Label>
                <Input
                  type="date"
                  value={reschedStart}
                  onChange={(e) => setReschedStart(e.target.value)}
                  data-testid="reschedule-start-date"
                  className="rounded-none bg-[#05050A] border-[#232330] font-mono"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">End date</Label>
                <Input
                  type="date"
                  value={reschedEnd}
                  onChange={(e) => setReschedEnd(e.target.value)}
                  data-testid="reschedule-end-date"
                  className="rounded-none bg-[#05050A] border-[#232330] font-mono"
                />
              </div>
              <Button
                onClick={rescheduleRound}
                data-testid="reschedule-submit"
                className="rounded-none bg-orange-500 hover:bg-orange-400 text-black uppercase tracking-widest text-xs font-bold h-10"
              >
                Push round
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between px-1 gap-3 flex-wrap">
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                Published rounds · {rounds.length}
              </div>
              {rounds.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadRoundsCsv(null)}
                  data-testid="rounds-export-all-csv"
                  title="Download every round + fixture as a referee hand-off CSV"
                  className="rounded-none border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/10 uppercase tracking-widest text-xs font-bold"
                >
                  <Download className="w-3.5 h-3.5 mr-2" /> Export all rounds CSV
                </Button>
              )}
            </div>
            {rounds.length === 0 && (
              <div className="border border-[#232330] p-8 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
                No rounds yet. Publish one above to open the reps' chat rooms.
              </div>
            )}
            {rounds.map((r) => {
              const rn = r.number;
              const expanded = roundExpand[rn];
              const fxList = roundFixtures[rn] || [];
              const loading = roundFixturesBusy[rn];
              const bulkBusy = bulkThreadBusy[rn];
              return (
                <div key={r.id} data-testid={`round-row-${r.id}`} className="border border-[#232330] bg-[#101015]">
                  <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => toggleRoundExpand(rn)}
                      data-testid={`round-expand-${r.id}`}
                      className="flex items-center gap-2 text-left"
                    >
                      {expanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                      <div>
                        <div className="font-heading font-black uppercase tracking-tight flex items-center gap-2 flex-wrap">
                          {r.name} <span className="text-gray-500">· #{r.number}</span>
                          {r.synthetic && (
                            <span
                              data-testid={`round-synth-${r.id}`}
                              title="From schedule — fixtures carry round_number but no explicit round was built here."
                              className="text-[9px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400/40 px-1.5 py-0.5"
                            >
                              from schedule
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-0.5">
                          {r.weekend_start ? new Date(r.weekend_start).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "TBD"} → {r.weekend_end ? new Date(r.weekend_end).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "TBD"} · {(r.fixture_ids || []).length} matchup{r.fixture_ids?.length === 1 ? "" : "s"}
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={() => openAllThreadsForRound(rn)}
                        disabled={bulkBusy}
                        data-testid={`round-open-all-threads-${r.id}`}
                        className="rounded-none bg-[#5865F2] hover:bg-[#4752c4] text-white uppercase tracking-widest text-xs font-bold"
                      >
                        {bulkBusy ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Opening…</>
                        ) : (
                          <><Hash className="w-3.5 h-3.5 mr-2" /> Open all threads</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadRoundsCsv(rn)}
                        data-testid={`round-export-csv-${r.id}`}
                        title="Download CSV for referee hand-off"
                        className="rounded-none border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/10 uppercase tracking-widest text-xs font-bold"
                      >
                        <Download className="w-3.5 h-3.5 mr-2" /> Export CSV
                      </Button>
                      {!r.synthetic && (
                        <Button
                          size="sm"
                          onClick={() => deleteRound(r)}
                          data-testid={`round-delete-${r.id}`}
                          variant="outline"
                          className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-xs"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete round
                        </Button>
                      )}
                    </div>
                  </div>
                  {expanded && (
                    <div className="border-t border-[#232330] p-4 space-y-2" data-testid={`round-fixtures-${r.id}`}>
                      {loading && (
                        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Loading fixtures…</div>
                      )}
                      {!loading && fxList.length === 0 && (
                        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">
                          No fixtures carry round_number = {rn}. If this round was built in the Season Generator, they should auto-link.
                        </div>
                      )}
                      {!loading && fxList.map((fx) => {
                        const home = teams.find((t) => t.id === fx.home_team_id);
                        const away = teams.find((t) => t.id === fx.away_team_id);
                        const hasThread = !!fx.discord_thread_id;
                        const busy = threadBusyPerFx[fx.id];
                        return (
                          <div
                            key={fx.id}
                            data-testid={`round-fixture-${fx.id}`}
                            className="border border-[#232330] bg-[#05050A] p-3 flex flex-wrap items-center justify-between gap-3"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-mono text-xs flex items-center gap-2">
                                <span className="text-white font-bold">{home?.tag || "TBD"}</span>
                                <span className="text-gray-600">vs</span>
                                <span className="text-white font-bold">{away?.tag || "TBD"}</span>
                                <span className="text-[9px] uppercase tracking-widest text-gray-500 ml-2">· {fx.status || "upcoming"}</span>
                              </div>
                              <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                                {fx.scheduled_at ? new Date(fx.scheduled_at).toLocaleString() : "TBD"} · {fx.map || "Wheelspin"}
                              </div>
                            </div>
                            {hasThread ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  data-testid={`fx-thread-open-${fx.id}`}
                                  className="text-[9px] font-mono uppercase tracking-widest text-[#00FFB2] border border-[#00FFB2]/40 px-2 py-1 inline-flex items-center gap-1"
                                >
                                  <Hash className="w-3 h-3" /> Thread open
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => recreateThread(fx)}
                                  disabled={busy}
                                  data-testid={`fx-recreate-thread-${fx.id}`}
                                  title="Deleted the thread on Discord by accident? Click to spawn a new one."
                                  className="rounded-none border-[#F4D03F]/40 text-[#F4D03F] hover:bg-[#F4D03F]/10 uppercase tracking-widest text-[10px] font-bold"
                                >
                                  {busy ? (
                                    <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Recreating…</>
                                  ) : (
                                    <><RefreshCw className="w-3 h-3 mr-1.5" /> Recreate</>
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => openSingleThread(fx)}
                                disabled={busy}
                                data-testid={`fx-open-thread-${fx.id}`}
                                className="rounded-none bg-[#5865F2] hover:bg-[#4752c4] text-white uppercase tracking-widest text-[10px] font-bold"
                              >
                                {busy ? (
                                  <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Opening…</>
                                ) : (
                                  <><Hash className="w-3 h-3 mr-1.5" /> Open thread</>
                                )}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {tab === "divisions" && (
        <section className="space-y-4" data-testid="admin-divisions">
          {/* Create form */}
          <div className="border border-[#232330] bg-[#101015] p-4">
            <div className="text-[10px] font-mono tracking-widest text-cyan-400 uppercase mb-3">New division</div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px_120px_140px] gap-2">
              <Input placeholder="Division name" value={divForm.name}
                     data-testid="div-name"
                     onChange={(e) => setDivForm({ ...divForm, name: e.target.value })}
                     className="rounded-none bg-[#05050A] border-[#232330]" />
              <Select value={divForm.tier} onValueChange={(v) => setDivForm({ ...divForm, tier: v })}>
                <SelectTrigger data-testid="div-tier" className="rounded-none bg-[#05050A] border-[#232330]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#101015] border-[#232330] text-white">
                  <SelectItem value="Mains">Mains</SelectItem>
                  <SelectItem value="Lite">Lite</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input type="color" value={divForm.color}
                       data-testid="div-color-picker"
                       onChange={(e) => setDivForm({ ...divForm, color: e.target.value })}
                       className="rounded-none bg-[#05050A] border-[#232330] p-1 h-9 w-16" />
                <select
                  value={divForm.color}
                  data-testid="div-color-preset"
                  onChange={(e) => setDivForm({ ...divForm, color: e.target.value })}
                  className="flex-1 h-9 rounded-none bg-[#05050A] border border-[#232330] text-xs font-mono px-2"
                >
                  {["#00F0FF","#FF007F","#F4D03F","#00FFB2","#B084FF","#7DF9FF","#FF4500","#39FF14","#FFD700","#DC143C","#00CED1","#9400D3","#FF6347","#20B2AA","#FF69B4","#8A2BE2","#00FA9A","#FF8C00","#4169E1","#DA70D6","#32CD32","#E6194B","#F58231"].map((c) => (
                    <option key={c} value={c} style={{ backgroundColor: c, color: "#000" }}>{c}</option>
                  ))}
                </select>
              </div>
              <Input type="number" placeholder="Order" value={divForm.sort_order}
                     onChange={(e) => setDivForm({ ...divForm, sort_order: Number(e.target.value) })}
                     className="rounded-none bg-[#05050A] border-[#232330] font-mono" />
              <Button onClick={createDivision} data-testid="div-create"
                      className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-xs font-bold">
                <Plus className="w-3.5 h-3.5 mr-2" /> Create
              </Button>
            </div>
          </div>

          {divisions.length === 0 && (
            <div className="border border-[#232330] p-8 text-center text-gray-500 font-mono text-xs">
              No divisions yet. Create your first bracket above.
            </div>
          )}

          {divisions.map((d) => {
            const inDiv = approvedTeams.filter((t) => d.team_ids?.includes(t.id));
            // Exclude teams already assigned to ANY division globally so no duplication
            const globallyAssigned = new Set(divisions.flatMap((dd) => dd.team_ids || []));
            const unassigned = approvedTeams.filter((t) => !globallyAssigned.has(t.id));
            return (
              <div key={d.id} data-testid={`division-${d.name.toLowerCase().replace(/\s+/g, '-')}`}
                   className="border border-[#232330] bg-[#101015] p-4" style={{ borderLeft: `4px solid ${d.color}` }}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-heading font-black text-xl uppercase tracking-tight flex items-center gap-2" style={{ color: d.color }}>
                      {d.name}
                      {d.season_locked && (
                        <span
                          data-testid={`division-locked-${d.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className="text-[9px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F] bg-[#F4D03F]/10 px-2 py-0.5 flex items-center gap-1"
                          title={d.season_locked_at ? `Season locked ${new Date(d.season_locked_at).toLocaleString()} by ${d.season_locked_by || 'admin'}` : "Season lockdown active"}
                        >
                          <Lock className="w-3 h-3" /> SEASON LOCKED
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">
                      {d.tier} · {inDiv.length} teams
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => toggleSeasonLock(d)}
                            data-testid={`season-lock-${d.name.toLowerCase().replace(/\s+/g, '-')}`}
                            variant="outline"
                            className={`rounded-none uppercase tracking-widest text-xs font-bold ${
                              d.season_locked
                                ? "border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black"
                                : "border-[#F4D03F] text-[#F4D03F] hover:bg-[#F4D03F] hover:text-black"
                            }`}>
                      {d.season_locked ? <><Unlock className="w-3.5 h-3.5 mr-1.5" /> Unlock season</> : <><Lock className="w-3.5 h-3.5 mr-1.5" /> Season lock</>}
                    </Button>
                    <Button size="sm" onClick={() => setAssignTargetDiv(d)}
                            data-testid={`assign-${d.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold">
                      <Plus className="w-3.5 h-3.5 mr-2" /> Assign team
                    </Button>
                    <Button size="sm" onClick={() => deleteDivision(d)} variant="outline"
                            className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-xs">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {inDiv.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {inDiv.map((t) => (
                      <span key={t.id} className="text-xs font-mono px-2 py-1 border border-[#232330] flex items-center gap-2">
                        <span style={{ color: t.logo_color }}>{t.tag}</span>
                        <span className="text-gray-500">{t.name}</span>
                        {divisions.length > 1 && (
                          <button
                            onClick={() => { setMoveTarget(t); setMoveDestDivId(""); }}
                            data-testid={`move-${t.tag}`}
                            className="text-gray-500 hover:text-cyan-400"
                            title="Move to another division"
                          >
                            <ArrowRightLeft className="w-3 h-3" />
                          </button>
                        )}
                        <button onClick={() => unassignTeam(d, t.id)}
                                data-testid={`unassign-${t.tag}`}
                                className="text-gray-500 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <Dialog open={assignTargetDiv?.id === d.id} onOpenChange={(o) => !o && setAssignTargetDiv(null)}>
                  <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
                    <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Assign to {d.name}</DialogTitle></DialogHeader>
                    <Label className="text-xs uppercase tracking-widest text-gray-400 mt-2">Team</Label>
                    <Select value={assignTeamId} onValueChange={setAssignTeamId}>
                      <SelectTrigger data-testid="assign-team-select" className="rounded-none bg-[#05050A] border-[#232330]">
                        <SelectValue placeholder="Pick a team" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#101015] border-[#232330] text-white">
                        {unassigned.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.tag} · {t.tier || "Mains"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button onClick={assignTeam} data-testid="confirm-assign"
                            className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest font-bold">
                      Assign
                    </Button>
                  </DialogContent>
                </Dialog>
              </div>
            );
          })}

          {/* Move team dialog */}
          <Dialog open={!!moveTarget} onOpenChange={(o) => { if (!o) { setMoveTarget(null); setMoveDestDivId(""); } }}>
            <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none" data-testid="move-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
                  <ArrowRightLeft className="w-4 h-4 text-cyan-400" />
                  Move {moveTarget?.tag || ""}
                </DialogTitle>
              </DialogHeader>
              {moveTarget && (() => {
                const currentDiv = divisions.find((d) => (d.team_ids || []).includes(moveTarget.id));
                const others = divisions.filter((d) => d.id !== currentDiv?.id);
                return (
                  <div className="space-y-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                      Currently in{" "}
                      <span style={{ color: currentDiv?.color }} className="font-bold">
                        {currentDiv?.name || "—"}
                      </span>{" "}
                      · {currentDiv?.tier || moveTarget.tier || "Mains"} tier
                    </div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">Move to</Label>
                    <Select value={moveDestDivId} onValueChange={setMoveDestDivId}>
                      <SelectTrigger data-testid="move-dest-select" className="rounded-none bg-[#05050A] border-[#232330]">
                        <SelectValue placeholder="Pick a destination division" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#101015] border-[#232330] text-white">
                        {others.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name} · {d.tier}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {moveDestDivId && (() => {
                      const dest = divisions.find((d) => d.id === moveDestDivId);
                      const tierChange = dest && currentDiv && dest.tier !== currentDiv.tier;
                      return tierChange ? (
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F]/40 p-2">
                          Heads up — team's tier will change from {currentDiv.tier} → {dest.tier}.
                        </div>
                      ) : null;
                    })()}
                    <Button
                      onClick={confirmMove}
                      disabled={!moveDestDivId}
                      data-testid="confirm-move"
                      className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold disabled:opacity-40"
                    >
                      Move team
                    </Button>
                  </div>
                );
              })()}
            </DialogContent>
          </Dialog>
        </section>
      )}

      {/* HLL Verification dialog */}
      <Dialog open={!!verifyFor} onOpenChange={(o) => !o && setVerifyFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <BadgeCheck className="w-4 h-4 text-cyan-400" />
              HLL verification · {verifyFor?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
            Open each player's HLL Records profile, confirm identity + stats, then flip the verify toggle.
            HLL Records is shield-protected — we deep-link out; use the manual refresh once the site is reachable.
          </p>
          <div className="max-h-[65vh] overflow-y-auto space-y-2">
            {verifyRows.length === 0 && (
              <div className="text-xs text-gray-500 font-mono p-8 text-center">No roster submitted yet.</div>
            )}
            {verifyRows.map((p) => {
              const link = p.hll_profile_url || p.hll_deep_link;
              return (
                <div key={p.id} data-testid={`verify-row-${p.handle}`}
                     className="border border-[#232330] bg-[#05050A] p-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-black text-sm uppercase tracking-tight">{p.handle}</span>
                      {p.player_id && <span className="text-[10px] font-mono text-gray-500">· {p.player_id}</span>}
                      {p.hll_verified && (
                        <span className="text-[9px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 px-1.5 py-0.5 flex items-center gap-1">
                          <BadgeCheck className="w-3 h-3" /> Verified
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-gray-500 mt-1 truncate max-w-[500px]">
                      {p.hll_profile_url || <span className="text-gray-600">No HLL URL — paste one below</span>}
                    </div>
                    {p.hll_stats && (p.hll_stats.kills != null || p.hll_stats.kd != null) && (
                      <div className="text-[10px] font-mono text-[#F4D03F] mt-1">
                        {p.hll_stats.kills ?? "-"}K / {p.hll_stats.deaths ?? "-"}D
                        {p.hll_stats.kd ? ` · ${p.hll_stats.kd} KD` : ""}
                        {p.hll_stats.winrate != null ? ` · ${p.hll_stats.winrate}% WR` : ""}
                        {p.hll_stats.source ? <span className="text-gray-500"> · {p.hll_stats.source}</span> : null}
                      </div>
                    )}
                    <div className="mt-2 flex gap-2">
                      <Input
                        placeholder="Paste HLL URL or player slug"
                        value={urlDraft[p.id] ?? ""}
                        data-testid={`verify-url-input-${p.handle}`}
                        onChange={(e) => setUrlDraft({ ...urlDraft, [p.id]: e.target.value })}
                        className="rounded-none bg-[#101015] border-[#232330] text-xs font-mono h-7"
                      />
                      <Button size="sm" onClick={() => saveHllUrl(p.id)}
                              data-testid={`verify-save-url-${p.handle}`}
                              className="rounded-none bg-[#232330] hover:bg-[#33334a] text-white uppercase tracking-widest text-[10px] h-7 px-2">
                        Save URL
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {link && (
                      <a href={link} target="_blank" rel="noreferrer"
                         data-testid={`verify-open-${p.handle}`}
                         className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300 border border-cyan-400/40 hover:border-cyan-300 px-3 py-1.5 flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Open records
                      </a>
                    )}
                    <Button size="sm" onClick={() => refreshHll(p.id)}
                            data-testid={`verify-refresh-${p.handle}`}
                            variant="outline"
                            disabled={!p.hll_profile_url}
                            className="rounded-none border-[#232330] hover:border-[#F4D03F] hover:text-[#F4D03F] uppercase tracking-widest text-[10px]">
                      Refresh stats
                    </Button>
                    <Button size="sm" onClick={() => toggleVerified(p.id)}
                            data-testid={`verify-toggle-${p.handle}`}
                            className={`rounded-none uppercase tracking-widest text-[10px] font-bold ${
                              p.hll_verified
                                ? "bg-transparent border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10"
                                : "bg-cyan-400 hover:bg-cyan-300 text-black"
                            }`}>
                      <BadgeCheck className="w-3 h-3 mr-1" /> {p.hll_verified ? "Unverify" : "Mark verified"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline reason dialog */}
      <Dialog open={!!declineFor} onOpenChange={(o) => !o && setDeclineFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Decline {declineFor?.name}
            </DialogTitle>
          </DialogHeader>
          <Label className="text-xs uppercase tracking-widest text-gray-400 mt-2">Reason (optional)</Label>
          <Textarea rows={3} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                    data-testid="decline-reason"
                    className="rounded-none bg-[#05050A] border-[#232330]" />
          <Button onClick={submitDecline} data-testid="confirm-decline"
                  className="rounded-none bg-red-500 hover:bg-red-400 text-white uppercase tracking-widest font-bold">
            Decline application
          </Button>
        </DialogContent>
      </Dialog>

      {/* Admin roster editor */}
      <Dialog open={!!rosterFor} onOpenChange={(o) => !o && setRosterFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Override roster · {rosterFor?.name} · {rosterFor?.tier || "Mains"} (cap {rosterFor?.roster_cap || 150})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-1 gap-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                Tick <span className="text-emerald-400">Rep</span> to grant access · email is optional, we auto-link on next login.
              </div>
              <div
                data-testid="admin-roster-rep-count"
                className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                  rosterEdit.filter((r) => r.is_rep).length > 4
                    ? "border-red-500 text-red-400 bg-red-500/10"
                    : "border-emerald-400/40 text-emerald-400 bg-emerald-400/5"
                }`}
              >
                Reps {rosterEdit.filter((r) => r.is_rep).length}/4
              </div>
            </div>
            {rosterEdit.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1.2fr_auto_36px] gap-2 items-end" data-testid={`admin-roster-row-${i}`}>
                <div>
                  {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Player name</Label>}
                  <Input value={p.handle}
                         data-testid={`admin-roster-handle-${i}`}
                         onChange={(e) => { const n = [...rosterEdit]; n[i] = { ...n[i], handle: e.target.value }; setRosterEdit(n); }}
                         className="rounded-none bg-[#05050A] border-[#232330] font-mono" />
                </div>
                <div>
                  {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Player ID</Label>}
                  <Input value={p.player_id}
                         data-testid={`admin-roster-player-id-${i}`}
                         onChange={(e) => { const n = [...rosterEdit]; n[i] = { ...n[i], player_id: e.target.value }; setRosterEdit(n); }}
                         className="rounded-none bg-[#05050A] border-[#232330] font-mono" />
                </div>
                <div>
                  {i === 0 && <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Email (optional)</Label>}
                  <Input type="email" value={p.email || ""}
                         data-testid={`admin-roster-email-${i}`}
                         onChange={(e) => { const n = [...rosterEdit]; n[i] = { ...n[i], email: e.target.value }; setRosterEdit(n); }}
                         placeholder="optional — auto-links on login"
                         className="rounded-none bg-[#05050A] border-[#232330] font-mono" />
                </div>
                <label
                  className={`h-9 px-3 border flex items-center gap-1.5 cursor-pointer transition-colors text-[10px] font-mono uppercase tracking-widest ${
                    p.is_rep
                      ? "bg-[#00FFB2] text-black border-[#00FFB2] font-bold"
                      : "text-gray-500 border-[#232330] hover:border-[#00FFB2] hover:text-[#00FFB2]"
                  }`}
                  data-testid={`admin-roster-rep-toggle-${i}`}
                >
                  <input
                    type="checkbox"
                    checked={!!p.is_rep}
                    onChange={(e) => {
                      const willBeRep = e.target.checked;
                      const currentRepCount = rosterEdit.filter((r, j) => j !== i && r.is_rep).length;
                      if (willBeRep && currentRepCount >= 4) {
                        toast.error("Max 4 reps per team. Un-flag another rep first.");
                        return;
                      }
                      const n = [...rosterEdit];
                      n[i] = { ...n[i], is_rep: willBeRep };
                      setRosterEdit(n);
                    }}
                    className="w-3 h-3 accent-emerald-400"
                  />
                  Rep
                </label>
                <button
                  onClick={() => setRosterEdit(rosterEdit.filter((_, j) => j !== i))}
                  className="h-9 border border-[#232330] hover:border-red-500 hover:text-red-500 text-gray-500 flex items-center justify-center"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {rosterEdit.length < (rosterFor?.roster_cap || 150) && (
              <button
                onClick={() => setRosterEdit([...rosterEdit, { handle: "", player_id: "", email: "", is_rep: false }])}
                data-testid="admin-roster-add"
                className="w-full py-2 border border-dashed border-[#232330] hover:border-[#FF007F] hover:text-[#FF007F] text-gray-500 text-xs uppercase tracking-widest font-mono transition-colors"
              >
                <Plus className="w-3.5 h-3.5 inline mr-2" /> Add player ({rosterEdit.length}/{rosterFor?.roster_cap || 150})
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => setRosterFor(null)}
              data-testid="admin-roster-cancel"
              variant="outline"
              className="rounded-none border-[#232330] hover:border-white uppercase tracking-widest font-bold"
            >
              Cancel
            </Button>
            <Button onClick={submitAdminRoster} data-testid="admin-roster-submit"
                    className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest font-bold flex-1">
              Overwrite roster
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
